import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import Select from '../../components/ui/Select';

import RecoveryChart from './components/RecoveryChart';
import RecoveryScoreCard from './components/RecoveryScoreCard';
import AddRecoveryModal from './components/AddRecoveryModal';

const RecoveryInsights = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [recoveryData, setRecoveryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [timeRange, setTimeRange] = useState('7');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadRecoveryData();
    }
  }, [user, currentTenant, timeRange]);

  const loadRecoveryData = async () => {
    setLoading(true);
    setError('');

    try {
      const daysAgo = parseInt(timeRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      const { data, error: fetchError } = await supabase
        ?.from('recovery_insights')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('date', startDate?.toISOString()?.split('T')?.[0])
        ?.order('date', { ascending: true });

      if (fetchError) throw fetchError;
      setRecoveryData(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load recovery data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverages = () => {
    if (recoveryData?.length === 0) return null;

    const totals = recoveryData?.reduce((acc, item) => ({
      sleep: acc?.sleep + (item?.sleep_hours || 0),
      hrv: acc?.hrv + (item?.heart_rate_variability || 0),
      rhr: acc?.rhr + (item?.resting_heart_rate || 0),
      score: acc?.score + (item?.recovery_score || 0),
      restDays: acc?.restDays + (item?.is_rest_day ? 1 : 0)
    }), { sleep: 0, hrv: 0, rhr: 0, score: 0, restDays: 0 });

    const count = recoveryData?.length;
    return {
      avgSleep: (totals?.sleep / count)?.toFixed(1),
      avgHRV: Math?.round(totals?.hrv / count),
      avgRHR: Math?.round(totals?.rhr / count),
      avgScore: Math?.round(totals?.score / count),
      restDays: totals?.restDays
    };
  };

  const averages = calculateAverages();
  const latestEntry = recoveryData?.[recoveryData?.length - 1];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Recovery Insights</h1>
            <p className="text-muted-foreground">Track your sleep, heart rate variability, and rest days for optimal recovery</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Entry
          </Button>
        </div>

        {/* Time Range Filter */}
        <div className="flex items-center gap-4 mb-6">
          <label className="text-sm font-medium text-foreground">Time Range:</label>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="w-40"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </Select>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading recovery data...</p>
            </div>
          </div>
        ) : recoveryData?.length === 0 ? (
          <div className="bg-card rounded-lg border border-border p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Activity" className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Recovery Data Yet</h3>
            <p className="text-muted-foreground mb-6">
              Start tracking your recovery metrics to optimize your training and performance
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Add Your First Entry
            </Button>
          </div>
        ) : (
          <>
            {/* Recovery Score Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <RecoveryScoreCard
                title="Recovery Score"
                value={latestEntry?.recovery_score || 0}
                average={averages?.avgScore}
                icon="Activity"
                color="primary"
                suffix="/100"
              />
              <RecoveryScoreCard
                title="Sleep Hours"
                value={latestEntry?.sleep_hours || 0}
                average={averages?.avgSleep}
                icon="Moon"
                color="blue"
                suffix="hrs"
              />
              <RecoveryScoreCard
                title="Heart Rate Variability"
                value={latestEntry?.heart_rate_variability || 0}
                average={averages?.avgHRV}
                icon="Heart"
                color="red"
                suffix="ms"
              />
              <RecoveryScoreCard
                title="Resting Heart Rate"
                value={latestEntry?.resting_heart_rate || 0}
                average={averages?.avgRHR}
                icon="Heartbeat"
                color="pink"
                suffix="bpm"
              />
              <RecoveryScoreCard
                title="Rest Days"
                value={averages?.restDays}
                average={null}
                icon="Calendar"
                color="green"
                suffix={`/${recoveryData?.length} days`}
              />
            </div>

            {/* Recovery Chart */}
            <div className="bg-card rounded-lg border border-border p-6 mb-8">
              <h2 className="text-lg font-semibold text-foreground mb-6">Recovery Trends</h2>
              <RecoveryChart data={recoveryData} />
            </div>

            {/* Recent Entries */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-6">Recent Entries</h2>
              <div className="space-y-4">
                {recoveryData?.slice()?.reverse()?.map(entry => (
                  <div
                    key={entry?.id}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-foreground">
                          {new Date(entry?.date)?.getDate()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(entry?.date)?.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-foreground">Recovery Score: {entry?.recovery_score}/100</span>
                          {entry?.is_rest_day && (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                              Rest Day
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Moon" size={12} />
                            {entry?.sleep_hours}hrs ({entry?.sleep_quality})
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Heart" size={12} />
                            HRV: {entry?.heart_rate_variability}ms
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Activity" size={12} />
                            RHR: {entry?.resting_heart_rate}bpm
                          </span>
                        </div>
                        {entry?.notes && (
                          <p className="text-xs text-muted-foreground mt-2">{entry?.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <MobileBottomNav />

      {showAddModal && (
        <AddRecoveryModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadRecoveryData();
          }}
        />
      )}
    </div>
  );
};

export default RecoveryInsights;